/**
 * RevenueSnapshot — compact funnel widget on the Today page.
 * Pipeline (weighted) → Contracted → Invoiced → Paid with conversion
 * ratios between stages. Values come from
 * GET /api/v2/workspaces/{ws}/revenue?period=this_quarter (funnel section).
 */
import { useEffect, useState } from "react";
import { fetchMyWorkspaces } from "@/lib/workspaceFetch";
import { useNavigate } from "react-router-dom";
import { TrendingUp, ArrowRight, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import RevenueFunnelDrillDrawer from "./RevenueFunnelDrillDrawer";
import CanonicalMoneyStates from "./CanonicalMoneyStates";
import { ttlCacheGet, ttlCacheSet, ttlCacheClear } from "../lib/ttlCache";
import { useWorkspace } from "../lib/workspaceContext";

// Same-origin in browser so the session cookie (scoped to the page's host) is sent on every API call.
// Falls back to the build-time env var only for SSR / localhost.
const _API_BASE = (typeof window !== 'undefined' && window.location.hostname !== 'localhost') ? window.location.origin : (process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : ''));
const API = `${_API_BASE}/api`;

const CURRENCY_OPTIONS = ["GBP", "NGN", "USD"];

export default function RevenueSnapshot() {
  const navigate = useNavigate();
  const { currency: ctxCurrency, setCurrency: setCtxCurrency } = useWorkspace();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drillStage, setDrillStage] = useState(null);
  const [workspaceId, setWorkspaceId] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  // Treat the global "All" selection as GBP for this widget (a mixed-currency
  // funnel defeats the point). We don't write back to context unless the user
  // clicks a pill — preserves their global preference elsewhere.
  const currency = CURRENCY_OPTIONS.includes(ctxCurrency) ? ctxCurrency : "GBP";

  useEffect(() => {
    let cancel = false;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000);
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const wsR_workspaces = await fetchMyWorkspaces({ signal: controller.signal });
        if (!Array.isArray(wsR_workspaces) || !wsR_workspaces.length) throw new Error("no_workspace");
        const wsId = wsR_workspaces[0].workspace_id;
        if (!cancel) setWorkspaceId(wsId);
        // 30s in-memory cache — keyed by (workspace, period, currency) so
        // toggling GBP↔NGN↔USD doesn't serve stale data from the other view.
        const cacheKey = `revenue:${wsId}:this_quarter:${currency}`;
        const cached = ttlCacheGet(cacheKey);
        if (cached) {
          if (!cancel) setData(cached);
          return;
        }
        const r = await fetch(`${API}/v2/workspaces/${wsId}/revenue?period=this_quarter&currency=${currency}`, { credentials: "include", signal: controller.signal });
        if (!r.ok) throw new Error("revenue_unavailable");
        const d = await r.json();
        ttlCacheSet(cacheKey, d, 30_000);
        if (!cancel) setData(d);
      } catch (e) {
        if (!cancel && e?.name !== "AbortError") {
          // AbortError fires legitimately on StrictMode double-mount
          // cleanup, route navigation, and the 8s timeout. None of
          // those are "fetch failed" — only log real failures.
          // eslint-disable-next-line no-console
          console.error("RevenueSnapshot fetch failed:", e);
          setError(e?.message || "error");
        } else if (!cancel && e?.name === "AbortError") {
          setError("timeout");
        }
      } finally {
        clearTimeout(timeoutId);
        if (!cancel) setLoading(false);
      }
    }
    run();
    return () => { cancel = true; controller.abort(); clearTimeout(timeoutId); };
  }, [currency, refreshTick]);

  // Listen for status changes triggered from the drill drawer or anywhere
  // else on the page, then refetch (bypassing TTL cache) so the funnel
  // tile reflects the new weighted pipeline immediately.
  useEffect(() => {
    const handler = () => {
      // Clear all cached revenue snapshots for this workspace so the
      // refetch hits the backend with fresh state.
      try { ttlCacheClear(`revenue:${workspaceId}:`); } catch { /* noop */ }
      setRefreshTick((t) => t + 1);
    };
    window.addEventListener("rex:opportunity-status-changed", handler);
    return () => window.removeEventListener("rex:opportunity-status-changed", handler);
  }, [workspaceId, currency]);

  const handleCurrencyChange = (next) => {
    if (next === currency) return;
    setCtxCurrency(next);
  };
  void handleCurrencyChange;

  // Pipeline-change toast feed — polls /api/pipeline-events/recent every
  // 30s + on focus and surfaces each undismissed event as a narrative
  // sonner toast ("Acme Corp moved to On Hold — ₦12.5M dropped from your
  // weighted pipeline (now ₦87.3M)"). Once shown, the event is dismissed
  // server-side so it never fires again.
  useEffect(() => {
    let cancelled = false;

    const fmtNGN = (n) => {
      const abs = Math.abs(n);
      if (abs >= 1e6) return `₦${(abs / 1e6).toFixed(1)}M`;
      if (abs >= 1e3) return `₦${(abs / 1e3).toFixed(0)}k`;
      return `₦${abs.toFixed(0)}`;
    };

    async function tick() {
      try {
        const r = await fetch(`${API}/pipeline-events/recent?limit=5`, {
          credentials: "include",
        });
        if (!r.ok) return;
        const { events = [] } = await r.json();
        if (cancelled || events.length === 0) return;

        // Show oldest first so the newest stays on top of the toast stack
        const ordered = [...events].sort((a, b) =>
          (a.created_at || "").localeCompare(b.created_at || "")
        );
        for (const ev of ordered) {
          const company = ev.company_name || "Client";
          const action = ev.action_label || "updated";
          const delta = Number(ev.delta || 0);
          const after = Number(ev.weighted_after || 0);
          const movement = delta < 0
            ? `${fmtNGN(delta)} dropped from your weighted pipeline`
            : `${fmtNGN(delta)} added to your weighted pipeline`;
          const tail = `now ${fmtNGN(after)}`;
          toast(`${company} ${action}`, {
            description: `${movement} (${tail})`,
            duration: 7000,
            id: ev.event_id, // de-dupe in the unlikely case of a repaint
          });
          // Dismiss server-side so we don't replay
          fetch(`${API}/pipeline-events/${ev.event_id}/dismiss`, {
            method: "POST",
            credentials: "include",
          }).catch(() => {});
        }
      } catch {
        /* network blips are silent */
      }
    }

    tick();
    const interval = setInterval(tick, 30_000);
    const onFocus = () => tick();
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, []);


  if (loading) {
    return (
      <div className="mb-4 rounded-xl border-2 border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 p-4 animate-pulse" data-testid="revenue-snapshot-loading">
        <div className="h-20 bg-emerald-100/60 rounded" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-4 flex items-start gap-3" data-testid="revenue-snapshot-empty">
        <AlertCircle className="h-5 w-5 text-slate-400 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-slate-700">Revenue snapshot unavailable</p>
          <p className="text-xs text-slate-500 mt-0.5">
            {error === "no_workspace"
              ? "You're not yet a member of a workspace — admin needs to assign you a role."
              : "No revenue data yet — add an Engagement and a Payment to see numbers here."}
            {" "}
            <button className="text-emerald-700 hover:underline" onClick={() => navigate("/engagements")}>
              Go to Engagements →
            </button>
          </p>
        </div>
      </div>
    );
  }

  const ccy = data?.filters?.currency || "GBP";

  return (
    <div className="mb-4 rounded-xl border-2 border-emerald-200 bg-gradient-to-r from-emerald-50/60 to-teal-50/40 p-4" data-testid="revenue-snapshot">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <TrendingUp className="h-5 w-5 text-emerald-600" />
        <h2 className="text-lg font-bold text-slate-900">Money — four states</h2>
        <button
          onClick={() => navigate("/dashboard?tab=revenue")}
          className="ml-auto text-xs text-emerald-700 hover:underline flex items-center gap-1"
          data-testid="revenue-snapshot-open-btn"
        >
          See full revenue <ArrowRight className="h-3 w-3" />
        </button>
      </div>

      <CanonicalMoneyStates compact />

      <RevenueFunnelDrillDrawer
        open={!!drillStage}
        onClose={() => setDrillStage(null)}
        stage={drillStage}
        workspaceId={workspaceId}
        period="this_quarter"
        currency={ccy}
        onOpportunityChanged={() => {
          try { ttlCacheClear(`revenue:${workspaceId}:`); } catch { /* noop */ }
          setRefreshTick((t) => t + 1);
        }}
      />
    </div>
  );
}
